import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Phone, Home, Building2, Droplets, TreePine, Car, Fence, ArrowRight } from 'lucide-react';

export const ServicesPage = () => {
  const { t } = useLanguage();

  const services = [
    { 
      icon: Home, 
      title: t('services.turnkey.title'), 
      description: t('services.turnkey.description'),
      image: "https://images.pexels.com/photos/323781/pexels-photo-323781.jpeg?auto=compress&cs=tinysrgb&w=800",
      featured: true
    },
    { 
      icon: Building2, 
      title: t('services.construction.title'), 
      description: t('services.construction.description'),
      image: "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    { 
      icon: Droplets, 
      title: t('services.pools.title'), 
      description: t('services.pools.description'),
      image: "https://images.pexels.com/photos/261185/pexels-photo-261185.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    { 
      icon: TreePine, 
      title: t('services.gazebos.title'), 
      description: t('services.gazebos.description'),
      image: "https://images.pexels.com/photos/7212850/pexels-photo-7212850.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    { 
      icon: Car, 
      title: t('services.garages.title'), 
      description: t('services.garages.description'),
      image: "https://images.pexels.com/photos/20548104/pexels-photo-20548104.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    { 
      icon: Fence, 
      title: t('services.terraces.title'), 
      description: t('services.terraces.description'),
      image: "https://images.unsplash.com/photo-1762608675427-09ac2dbd1540?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
    }
  ];

  return (
    <main className="pt-24" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-[#0B1E3B] py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
            {t('services.tagline')}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white font-['Barlow_Condensed'] mb-4">
            {t('services.title')}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-white py-20" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 ${
                  service.featured ? 'md:col-span-2' : ''
                }`}
                data-testid={`service-item-${index}`}
              >
                {/* Background Image */}
                <div className={`relative ${service.featured ? 'h-80' : 'h-64'}`}>
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E3B] via-[#0B1E3B]/50 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                      <service.icon className="h-5 w-5 text-[#0B1E3B]" />
                    </div>
                    <h3 className="text-2xl font-bold font-['Barlow_Condensed']">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-200 text-sm leading-relaxed max-w-xl">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#F8F9FA] py-16" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0B1E3B] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Barlow_Condensed'] mb-2">
                {t('contact.area.title')}
              </h2>
              <p className="text-gray-300">
                {t('contact.area.description')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+381631627662">
                <Button 
                  size="lg"
                  className="bg-[#D4AF37] text-[#0B1E3B] hover:bg-[#D4AF37]/90 font-semibold"
                  data-testid="services-cta-phone"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  +381 63 162 7662
                </Button>
              </a>
              <Link to="/kontakt">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-[#0B1E3B] font-semibold"
                  data-testid="services-cta-contact"
                >
                  {t('hero.cta_contact')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
